import React, { useState } from 'react';
import { Paper, TextField, Grid, Typography, Divider, Box, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const ContactTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  // Campos de contact - solo los que son configurables
  const contactFields = [
    { path: 'siteConfig.contact.title', label: t('contact_title') || 'Título' },
    { path: 'siteConfig.contact.subtitle', label: t('contact_subtitle') || 'Subtítulo' },
    { path: 'siteConfig.contact.email', label: t('contact_email') || 'Email', type: 'email' },
    { path: 'siteConfig.contact.phone', label: t('contact_phone') || 'Teléfono' },
    { path: 'siteConfig.contact.address', label: t('contact_address') || 'Dirección' },
    { path: 'siteConfig.contact.formFields.namePlaceholder', label: t('name_placeholder') || 'Placeholder Nombre' },
    { path: 'siteConfig.contact.formFields.emailPlaceholder', label: t('email_placeholder') || 'Placeholder Email' },
    { path: 'siteConfig.contact.formFields.messagePlaceholder', label: t('message_placeholder') || 'Placeholder Mensaje' },
    { path: 'siteConfig.contact.formFields.submitText', label: t('submit_text') || 'Texto del Botón' }
  ];
  
  // Filtrar solo los campos configurables
  const fields = contactFields.filter(field => isFieldConfigurable(field.path));

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Preparar updates solo para campos configurables (ya están filtrados)
      const updates = {};
      fields.forEach(field => {
        const value = getFieldValue(field.path);
        updates[field.path] = value;
      });

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving contact:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const getFieldValue = (path) => {
    const value = path.split('.').reduce((obj, key) => obj?.[key], config);
    // Retornar el valor o string vacío si es null/undefined
    return value !== null && value !== undefined ? value : '';
  };

  const hasEditableFields = fields.some(field => isFieldConfigurable(field.path));

  const formFieldsStartIndex = fields.findIndex(f => f.path.includes('formFields'));
  const mainFields = fields.slice(0, formFieldsStartIndex);
  const formFields = fields.slice(formFieldsStartIndex);

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {leadingContent}
      {!hasEditableFields && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      )}
      <Grid container spacing={2}>
        {mainFields.map((field) => {
          const isConfigurable = isFieldConfigurable(field.path);
          const isFixed = isFieldFixed(field.path);
          const value = getFieldValue(field.path);

          if (!isConfigurable && !isFixed) return null;

          return (
            <Grid item xs={12} md={4} key={field.path}>
              <TextField
                fullWidth
                label={field.label}
                value={value}
                onChange={(e) => handleChange(field.path, e.target.value)}
                disabled={!isConfigurable || isFixed}
                type={field.type || 'text'}
                helperText={isFixed ? (t('field_fixed') || 'Este campo es fijo y no se puede modificar') : ''}
              />
            </Grid>
          );
        })}
        {formFields.some(f => isFieldConfigurable(f.path) || isFieldFixed(f.path)) && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('form_fields') || 'Campos del Formulario'}</Typography>
            </Grid>
            {formFields.map((field) => {
              const isConfigurable = isFieldConfigurable(field.path);
              const isFixed = isFieldFixed(field.path);
              const value = getFieldValue(field.path);

              if (!isConfigurable && !isFixed) return null;

              return (
                <Grid item xs={12} md={6} key={field.path}>
                  <TextField
                    fullWidth
                    label={field.label}
                    value={value}
                    onChange={(e) => handleChange(field.path, e.target.value)}
                    disabled={!isConfigurable || isFixed}
                    helperText={isFixed ? (t('field_fixed') || 'Este campo es fijo y no se puede modificar') : ''}
                  />
                </Grid>
              );
            })}
          </>
        )}
        {hasEditableFields && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default ContactTab;
